/**
 * Frontend validation utilities for business identifiers.
 * These mirror the backend validation logic in src/utils/validation.ts
 * without the Joi dependency.
 */

/**
 * PAN entity type mapping: 4th character of PAN indicates entity type
 */
const PAN_ENTITY_TYPES: Record<string, string> = {
  P: 'Person',
  C: 'Company',
  H: 'HUF',
  F: 'Firm',
  A: 'AOP',
  T: 'Trust',
  B: 'BOI',
  L: 'Local Authority',
  J: 'Artificial Juridical Person',
  G: 'Government',
}

/**
 * Validates PAN (Permanent Account Number) format with entity-type checking.
 */
export function isValidPAN(value: string): { valid: boolean; entityType?: string; error?: string } {
  if (value.length !== 10) {
    return { valid: false, error: 'Invalid length' }
  }

  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/
  if (!panRegex.test(value)) {
    return { valid: false, error: 'Invalid format' }
  }

  const entityChar = value[3]
  const entityType = PAN_ENTITY_TYPES[entityChar]
  if (!entityType) {
    return { valid: false, error: 'Invalid entity type character' }
  }

  return { valid: true, entityType }
}

/**
 * Valid Indian state codes for GSTIN (positions 1-2)
 */
const VALID_GSTIN_STATE_CODES = new Set([
  '01', '02', '03', '04', '05', '06', '07', '08', '09', '10',
  '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
  '21', '22', '23', '24', '25', '26', '27', '28', '29', '30',
  '31', '32', '33', '34', '35', '36', '37', '97',
])

function gstinCharValue(char: string): number {
  const code = char.charCodeAt(0)
  if (code >= 48 && code <= 57) {
    return code - 48
  }
  return code - 55
}

function computeGSTINCheckDigit(gstin: string): string {
  let totalSum = 0
  for (let i = 0; i < 14; i++) {
    const charVal = gstinCharValue(gstin[i])
    const factor = i % 2 === 0 ? 1 : 2
    const product = charVal * factor
    const quotient = Math.floor(product / 36)
    const remainder = product % 36
    totalSum += quotient + remainder
  }
  const checkValue = (36 - (totalSum % 36)) % 36
  if (checkValue < 10) {
    return String(checkValue)
  }
  return String.fromCharCode(55 + checkValue)
}

/**
 * Validates GSTIN with full checksum verification.
 */
export function isValidGSTIN(value: string): { valid: boolean; error?: string } {
  if (value.length !== 15) {
    return { valid: false, error: 'Invalid length' }
  }

  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][Z][0-9A-Z]$/
  if (!gstinRegex.test(value)) {
    return { valid: false, error: 'Invalid format' }
  }

  const stateCode = value.substring(0, 2)
  if (!VALID_GSTIN_STATE_CODES.has(stateCode)) {
    return { valid: false, error: 'Invalid state code' }
  }

  const embeddedPAN = value.substring(2, 12)
  if (!isValidPAN(embeddedPAN).valid) {
    return { valid: false, error: 'Invalid embedded PAN' }
  }

  const expectedCheckDigit = computeGSTINCheckDigit(value)
  const actualCheckDigit = value[14]
  if (actualCheckDigit !== expectedCheckDigit) {
    return { valid: false, error: 'Checksum mismatch' }
  }

  return { valid: true }
}

/**
 * Valid Indian state/UT abbreviations for Udyam registration numbers.
 */
const VALID_UDYAM_STATE_CODES = new Set([
  'AN', 'AP', 'AR', 'AS', 'BR', 'CG', 'CH', 'DD', 'DL', 'GA',
  'GJ', 'HP', 'HR', 'JH', 'JK', 'KA', 'KL', 'LA', 'LD', 'MH',
  'ML', 'MN', 'MP', 'MZ', 'NL', 'OD', 'PB', 'PY', 'RJ', 'SK',
  'TN', 'TS', 'TR', 'UK', 'UP', 'WB',
])

/**
 * Validates Udyam registration number format with state-code verification.
 */
export function isValidUdyam(value: string): { valid: boolean; stateCode?: string; error?: string } {
  const udyamRegex = /^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/
  if (!udyamRegex.test(value)) {
    return { valid: false, error: 'Invalid format' }
  }

  const stateCode = value.substring(6, 8)
  if (!VALID_UDYAM_STATE_CODES.has(stateCode)) {
    return { valid: false, error: 'Invalid state code' }
  }

  const districtCode = value.substring(9, 11)
  const districtNum = parseInt(districtCode, 10)
  if (districtNum < 1 || districtNum > 99) {
    return { valid: false, error: 'Invalid district code' }
  }

  return { valid: true, stateCode }
}

/**
 * Auto-detects the identifier type based on input prefix patterns.
 */
export function detectIdentifierType(input: string): 'gstin' | 'pan' | 'udyam' | null {
  if (!input || input.length < 2) {
    return null
  }

  if (/^UD/i.test(input)) {
    return 'udyam'
  }

  if (/^[0-9]{2}[A-Za-z]/.test(input)) {
    return 'gstin'
  }

  if (/^[A-Z]{5}/.test(input)) {
    return 'pan'
  }

  return null
}

/**
 * Validates an identifier based on its detected type.
 * Returns a user-friendly error message or null if valid.
 */
export function validateIdentifier(
  value: string,
  type: 'gstin' | 'pan' | 'udyam' | 'cin' | null
): string | null {
  if (!value.trim()) {
    return 'Please enter a business identifier'
  }

  if (!type) {
    return 'Unable to detect identifier type. Supported formats: GSTIN (15 chars), PAN (10 chars), Udyam (UDYAM-XX-00-0000000)'
  }

  switch (type) {
    case 'gstin': {
      const result = isValidGSTIN(value.toUpperCase())
      if (!result.valid) {
        switch (result.error) {
          case 'Invalid length':
            return 'GSTIN must be exactly 15 characters'
          case 'Invalid format':
            return 'GSTIN format: 2 digits + 10 char PAN + entity code + Z + check digit'
          case 'Invalid state code':
            return 'Invalid state code in GSTIN (first 2 digits)'
          case 'Invalid embedded PAN':
            return 'Invalid PAN embedded in GSTIN (characters 3-12)'
          case 'Checksum mismatch':
            return 'GSTIN checksum verification failed'
          default:
            return 'Invalid GSTIN format'
        }
      }
      return null
    }
    case 'pan': {
      const result = isValidPAN(value.toUpperCase())
      if (!result.valid) {
        switch (result.error) {
          case 'Invalid length':
            return 'PAN must be exactly 10 characters'
          case 'Invalid format':
            return 'PAN format: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)'
          case 'Invalid entity type character':
            return 'Invalid entity type in PAN (4th character must be P, C, H, F, A, T, B, L, J, or G)'
          default:
            return 'Invalid PAN format'
        }
      }
      return null
    }
    case 'udyam': {
      const result = isValidUdyam(value.toUpperCase())
      if (!result.valid) {
        switch (result.error) {
          case 'Invalid format':
            return 'Udyam format: UDYAM-XX-00-0000000 (e.g., UDYAM-MH-01-0000001)'
          case 'Invalid state code':
            return 'Invalid state code in Udyam number'
          case 'Invalid district code':
            return 'Invalid district code in Udyam number'
          default:
            return 'Invalid Udyam format'
        }
      }
      return null
    }
    case 'cin': {
      const upper = value.toUpperCase()
      if (upper.length !== 21) {
        return 'CIN must be exactly 21 characters'
      }
      if (!/^[A-Z][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(upper)) {
        return 'Invalid CIN format (e.g., U72900KA2015PTC082988)'
      }
      return null
    }
    default:
      return 'Unsupported identifier type'
  }
}
